import React from "react";
import { Box, Stack, Typography, IconButton, Chip } from "@mui/material";
import ReactCountryFlag from "react-country-flag";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import colors from "../../../../utils/colors";

export const PRIMARY_COLOR = "#6371F7";
export const TEXT_PRIMARY = "#1A1A1A";
export const TEXT_SECONDARY = "#6B7280";
export const BORDER_COLOR = "#E5E7EB";
export const ERROR_COLOR = "#DC2626";

export function formatDrawerFooterLabel(siteLabel, host) {
  const a = siteLabel != null ? String(siteLabel).trim() : "";
  const b = host != null ? String(host).trim() : "";
  if (!a && !b) return "";
  if (a && b && a.toLowerCase() === b.toLowerCase()) return a;
  if (a && b) return `${a} · ${b}`;
  return a || b;
}

export const baseFontStyle = {
  fontSize: 14,
  lineHeight: "30px",
};

export const valueTypographySx = {
  fontSize: 12.5,
  lineHeight: 1.35,
  color: PRIMARY_COLOR,
  wordBreak: "break-all",
  fontWeight: 500,
};

const requestInfoLabelSx = {
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#8A8FA8",
};

export const secondaryButtonStyle = {
  ...baseFontStyle,
  height: 36,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: "#fff",
  border: `1px solid ${BORDER_COLOR}`,
  color: TEXT_PRIMARY,
  "&:hover": {
    backgroundColor: "#fff",
    borderColor: PRIMARY_COLOR,
    color: TEXT_PRIMARY,
  },
};

export const primaryButtonStyle = {
  ...baseFontStyle,
  height: 36,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: PRIMARY_COLOR,
  color: "#fff",
  border: `1px solid ${PRIMARY_COLOR}`,
  "&:hover": {
    backgroundColor: "#5563E8",
    borderColor: "#5563E8",
    color: "#fff",
  },
  "&.Mui-disabled": {
    backgroundColor: colors.neutral[200],
    borderColor: colors.neutral[200],
    color: colors.neutral[500],
  },
};

export function isBlockedAction(action) {
  const key = String(action || "").toLowerCase();
  return key === "block" || key === "blocked";
}

export const BLOCKED_STATUS_FG = colors.red[100];
export const BLOCKED_STATUS_CHIP = {
  fg: colors.red[200],
  bg: colors.red[10],
  border: "rgba(251,55,72,0.30)",
};

export const getStatusConfig = (status) => {
  switch (status) {
    case "Blocked":
      return { textColor: "#B91C1C", bgColor: "#FEE2E2" };
    case "Rate Limited":
      return { textColor: "#7C3AED", bgColor: "#EDE9FE" };
    case "Challenged":
      return { textColor: "#92400E", bgColor: "#FEF3C7" };
    case "Passed":
    case "Detect":
      return { textColor: "#065F46", bgColor: "#D1FAE5" };
    default:
      return { textColor: TEXT_SECONDARY, bgColor: "#F3F4F6" };
  }
};

export const getRiskLevelConfig = (riskLevel) => {
  switch (riskLevel?.toLowerCase()) {
    case "malicious":
      return { color: "#DC2626", bgColor: "#FEE2E2", label: "Malicious" };
    case "suspicious":
      return { color: "#C2410C", bgColor: "#FFEDD5", label: "Suspicious" };
    case "critical":
      return { color: "#DC2626", bgColor: "#FEE2E2", label: "Critical" };
    case "high":
      return { color: "#EA580C", bgColor: "#FFEDD5", label: "High" };
    case "medium":
      return { color: "#CA8A04", bgColor: "#FEF9C3", label: "Medium" };
    case "low":
      return { color: "#16A34A", bgColor: "#DCFCE7", label: "Low" };
    default:
      return { color: TEXT_SECONDARY, bgColor: "#F3F4F6", label: riskLevel || "Unknown" };
  }
};

const formatConfidenceLabel = (pct) => String(Math.round(Number(pct)));

const GAUGE_GREEN = "#16A34A";
const GAUGE_TRACK = "#E8EAED";

const parseGaugePercent = (raw) => {
  if (raw === undefined || raw === null || raw === "") return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.min(100, Math.max(0, raw));
  const n = parseFloat(String(raw).replace(/%/g, "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0;
};

export function ConfidenceGauge({ value }) {
  const pct = parseGaugePercent(value);
  const cx = 100;
  const cy = 80;
  const r = 64;
  const strokeW = 10;

  const clockXY = (deg) => ({
    x: cx + r * Math.sin((deg * Math.PI) / 180),
    y: cy - r * Math.cos((deg * Math.PI) / 180),
  });

  const pS = clockXY(240);
  const pE = clockXY(120);
  const arcLen = (240 / 360) * 2 * Math.PI * r;
  const arcPath = `M ${pS.x.toFixed(2)} ${pS.y.toFixed(2)} A ${r} ${r} 0 1 1 ${pE.x.toFixed(2)} ${pE.y.toFixed(2)}`;
  const dashOffset = arcLen * (1 - pct / 100);
  const tickPt = clockXY(105);
  const label = formatConfidenceLabel(pct);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      <Box sx={{ position: "relative", width: 156, height: 110 }}>
        <svg width="156" height="100" viewBox="0 0 200 128" style={{ display: "block" }} aria-hidden>
          <path d={arcPath} fill="none" stroke={GAUGE_TRACK} strokeWidth={strokeW} strokeLinecap="round" />
          <path
            d={arcPath}
            fill="none"
            stroke={GAUGE_GREEN}
            strokeWidth={strokeW}
            strokeLinecap="round"
            strokeDasharray={`${arcLen} ${arcLen}`}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.45s ease" }}
          />
          <line x1={tickPt.x - 5} y1={tickPt.y} x2={tickPt.x + 5} y2={tickPt.y} stroke="#374151" strokeWidth={2} strokeLinecap="round" />
        </svg>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 48,
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            gap: "2px",
            pointerEvents: "none",
          }}
        >
          <Typography component="span" sx={ { fontWeight: 700, fontSize: 28, lineHeight: 1, color: TEXT_PRIMARY }}>
            {label}
          </Typography>
          <Typography component="span" sx={ { fontWeight: 600, fontSize: 11, lineHeight: 1, color: TEXT_PRIMARY }}>
            %
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function RequestInfoBox({
  label,
  value,
  isStatus = false,
  statusConfig = null,
  copyable = false,
  onCopy = null,
  monospace = false,
}) {
  return (
    <Stack spacing={0.5}>
      <Typography sx={requestInfoLabelSx}>{label}</Typography>
      {isStatus && statusConfig ? (
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.textColor,
            fontSize: 13,
            fontWeight: 500,
            height: 24,
            borderRadius: "8px",
            width: "fit-content",
          }}
        />
      ) : copyable && onCopy ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            sx={{
              ...valueTypographySx,
              ...(monospace ? { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" } : {}),
            }}
          >
            {value || "—"}
          </Typography>
          <IconButton size="small" onClick={() => onCopy(value)} sx={{ p: 0.5, "&:hover": { backgroundColor: "#EEF0FE" } }}>
            <LayerdIcon icon={DETECTION_ICONS.copy} size={14} color="primary" />
          </IconButton>
        </Stack>
      ) : (
        <Typography
          sx={{
            ...valueTypographySx,
            ...(monospace ? { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" } : {}),
          }}
        >
          {value || "—"}
        </Typography>
      )}
    </Stack>
  );
}

export const tagChipSx = {
  fontSize: 13,
  fontWeight: 500,
  height: 28,
  borderRadius: "8px",
  px: 1,
};

/** Compact chips matching reference `.ctag` (26px, 11.5px) */
export const drawerTagChipSx = {
  fontSize: 11.5,
  fontWeight: 500,
  height: 26,
  borderRadius: "999px",
  "& .MuiChip-label": { px: "11px", py: 0, lineHeight: 1.2 },
  "& .MuiChip-icon": { width: 12, height: 12, ml: "6px" },
};

export const DRAWER_SEVERITY_PALETTE = {
  critical: {
    label: "Critical",
    fg: colors.red[200],
    bg: colors.red[10],
    border: "rgba(251,55,72,0.30)",
  },
  high: {
    label: "High",
    fg: "#D00416",
    bg: "rgba(251,55,72,0.12)",
    border: "rgba(251,55,72,0.32)",
  },
  medium: {
    label: "Medium",
    fg: "#7a5b10",
    bg: "rgba(223,180,0,0.16)",
    border: "rgba(223,180,0,0.45)",
  },
  low: {
    label: "Low",
    fg: colors.green[300],
    bg: "rgba(31,193,107,0.12)",
    border: "rgba(31,193,107,0.32)",
  },
};

const NEUTRAL_DRAWER_CHIP = {
  fg: TEXT_PRIMARY,
  bg: "#F3F4F6",
  border: BORDER_COLOR,
  label: "—",
};

export function drawerSeverityPalette(severity) {
  if (!severity) return NEUTRAL_DRAWER_CHIP;
  const key = String(severity).toLowerCase();
  return DRAWER_SEVERITY_PALETTE[key] || { ...NEUTRAL_DRAWER_CHIP, label: String(severity) };
}

const BLOCKED_ACTION_CHIP = { fg: colors.red[200], bg: colors.red[10], border: "rgba(251,55,72,0.30)" };

const ACTION_CHIP_PALETTE = {
  block: BLOCKED_ACTION_CHIP,
  blocked: BLOCKED_ACTION_CHIP,
  challenge: { fg: "#b45309", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)" },
  detect: { fg: colors.green[300], bg: colors.green[10], border: "rgba(31,193,107,0.32)" },
  allow: { fg: colors.green[300], bg: colors.green[10], border: "rgba(31,193,107,0.32)" },
  log_only: { fg: TEXT_PRIMARY, bg: "#F3F4F6", border: BORDER_COLOR },
  skipped: { fg: TEXT_SECONDARY, bg: "#F3F4F6", border: BORDER_COLOR },
};

export function drawerActionChipSx(action) {
  const key = String(action || "").toLowerCase();
  const palette = ACTION_CHIP_PALETTE[key] || { fg: TEXT_SECONDARY, bg: "#F3F4F6", border: BORDER_COLOR };
  return {
    ...drawerTagChipSx,
    backgroundColor: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
  };
}

export function drawerSeverityChipSx(severity, { compact = false } = {}) {
  const palette = drawerSeverityPalette(severity);
  const base = compact
    ? {
      height: 22,
      fontSize: 11,
      fontWeight: 500,
      borderRadius: "8px",
      "& .MuiChip-label": { px: 1, lineHeight: "20px" },
    }
    : tagChipSx;

  return {
    ...base,
    backgroundColor: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
  };
}

export const drawerNeutralChipSx = {
  ...drawerTagChipSx,
  backgroundColor: "rgba(40, 46, 100, 0.025)",
  color: "#565B76",
  border: "1px solid rgba(55, 62, 120, 0.14)",
  fontFamily: "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: 11,
};

export const drawerPrimaryChipSx = {
  ...drawerTagChipSx,
  backgroundColor: "rgba(99, 113, 247, 0.10)",
  color: "#4E5AD6",
  border: "1px solid rgba(99, 113, 247, 0.34)",
  fontWeight: 600,
};

